import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, LinearProgress, Paper, Button } from "@mui/material";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import heartLoading from "../../../assests/heartLoading.gif";
import Plot from "react-plotly.js";
import ProfilingResultsTable from "./ProfilingResultsTable";

const DataProfiling = ({ onPrevious, nextStep, id }) => {
  const [profilingStatusLoading, setProfilingStatusLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [profilingResults, setProfilingResults] = useState([]);
  const [businessTerms, setBusinessTerms] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [popupAssetName, setPopupAssetName] = useState("");
  const [profilingResultStatus, setProfilingResultStatus] = useState("");
  const [currentRowCount, setCurrentRowCount] = useState("");
  const [graphOpen, setGraphOpen] = useState(false);
  const [graphColumn, setGraphColumn] = useState(null);
  const [error, setError] = useState("");
  const [profilingJobStatus, setProfilingJobStatus] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [completedFiles, setCompletedFiles] = useState(0);
  const [finalResult, setFinalResult] = useState(false);
  const [isReRunning, setIsReRunning] = useState(false);
  const isFetchingResults = useRef(false);
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState("Initializing");
  const totalExpectedTime = 15 * 60; // 15 minutes in seconds
  const initialPhaseTime = 20; // seconds for first 3%
  const phases = [
    { name: "Initializing", maxProgress: 5 },
    { name: "Scanning data sources", maxProgress: 15 },
    { name: "Analyzing data structure", maxProgress: 35 },
    { name: "Profiling data quality", maxProgress: 55 },
    { name: "Generating insights", maxProgress: 75 },
    { name: "Finalizing analysis", maxProgress: 85 },
  ];
  useEffect(() => {
    if (!profilingStatusLoading) return;
    let start = Date.now();
    let animation;
    function animate() {
      const elapsed = (Date.now() - start) / 1000; // seconds
      let pct;
      if (elapsed < initialPhaseTime) {
        pct = (elapsed / initialPhaseTime) * 3; // 0-3% in first 20s
      } else {
        const remainingTime = totalExpectedTime - initialPhaseTime;
        const elapsedAfterInitial = elapsed - initialPhaseTime;
        pct = 3 + (elapsedAfterInitial / remainingTime) * (85 - 3);
      }
      pct = Math.min(pct, 85);
      setProgress(pct);
      // Set phase
      const phase =
        phases.find((ph) => pct <= ph.maxProgress) || phases[phases.length - 1];
      setCurrentPhase(phase.name);
      if (pct < 85) {
        animation = requestAnimationFrame(animate);
      }
    }
    animation = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animation);
  }, [profilingStatusLoading]);
  // Calculate remaining time
  const remainingSeconds = Math.max(
    0,
    Math.round(((85 - progress) / 85) * totalExpectedTime)
  );
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  // --- END Loading Progress State ---

  const fetchResults = async () => {
    // Prevent duplicate calls
    if (isFetchingResults.current) {
      return;
    }

    isFetchingResults.current = true;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/profiling_results/${id}`
      );
      if (response?.status === 200 || response?.status === 201) {
        setProfilingResults(response?.data?.profiling_results);
        setFinalResult(true);
        toast.success("Profiling results loaded successfully!");
      }
    } catch (err) {
      setError("Failed to load profiling results.");
      toast.error("Failed to load profiling results.");
    } finally {
      isFetchingResults.current = false;
    }
  };

  const pollStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-profiling-job-status/${id}`
      );

      // Update profiling job status
      if (response?.data?.profiling_job_status) {
        setProfilingJobStatus(response.data.profiling_job_status);
        setTotalFiles(response.data.profiling_job_status.length);

        // Count completed files
        const completed = response.data.profiling_job_status.filter(
          (job) => job.status === "Completed"
        ).length;
        setCompletedFiles(completed);
      }

      if (response?.data?.overall_status === "Completed") {
        setProfilingResultStatus("Completed");
        setProfilingStatusLoading(false);
        // Fetch results once when completed
        if (!finalResult) {
          setResultsLoading(true);
          await fetchResults();
          setResultsLoading(false);
        }
      } else if (
        response?.data?.overall_status === "In Progress" ||
        response?.data?.overall_status === "Pending"
      ) {
        setProfilingResultStatus(response?.data?.overall_status);
        setProfilingStatusLoading(true);
        // Continue polling every 60 seconds
        setTimeout(pollStatus, 60000);
      } else if (response?.data?.overall_status === "Failed") {
        setProfilingResultStatus("Failed");
        setProfilingStatusLoading(false);
        setError("Profiling job failed.");
      }
    } catch (err) {
      setProfilingStatusLoading(false);
      setError("Failed to poll profiling status.");
    }
  };

  const areAllBusinessTermsFilled = () => {
    return Object.values(businessTerms).every((term) => term.trim() !== "");
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      const payload = profilingResults.map((asset) => ({
        assetName: asset.assetName,
        columns: asset.columns.map((col) => ({
          columnName: col.columnName,
          dataType: col.dataType,
          businessTerm:
            businessTerms[`${asset.assetName}-${col.columnName}`] || "",
          nullPercentage: col.nullPercentage,
          uniquePercentage: col.uniquePercentage,
          distribution: col.dataDistribution,
        })),
      }));
      await axios.post(
        `${process.env.REACT_APP_API_URL}/save-profiling-draft`,
        payload
      );
      toast.success("Draft saved successfully!");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReadyForDQ = async () => {
    if (!areAllBusinessTermsFilled()) {
      toast.error("Please fill in all business terms before proceeding.");
      return;
    }
    // Build payload
    const payload = { ProducId: id };
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/dataQuality/start/${id}`
      );
      if (response.status === 200 || 201) {
        toast.success("Rule initialization successful");
        nextStep();
      }
    } catch (error) {
      console.error("DQ Checks API failed (expected):", error);
      toast.error("DQ Checks API failed (but proceeding)...");
    }
  };

  const handleReRunProfiling = async () => {
    setIsReRunning(true);
    setError("");
    try {
      // Trigger profiling API
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/trigger_profiling/${id}`
      );
      if (response.status === 200 || response.status === 201) {
        toast.success("Profiling restarted successfully!");
        // Wait 2 seconds before starting polling
        setTimeout(() => {
          setIsReRunning(false);
          setProfilingStatusLoading(true);
          setProfilingResultStatus("Pending");
          setFinalResult(false);
          setProfilingResults([]);
          setCompletedFiles(0);
          setTotalFiles(0);
          // Trigger the polling flow by calling pollStatus
          pollStatus();
        }, 2000);
      }
    } catch (error) {
      console.error("Failed to restart profiling:", error);
      toast.error("Failed to restart profiling. Please try again.");
      setIsReRunning(false);
    }
  };

  useEffect(() => {
    // Start polling only if not re-running
    if (!isReRunning) {
      pollStatus();
    }
  }, [id, isReRunning]);

  const handleViewPlot = useCallback((column) => {
    setGraphOpen(true);
    setGraphColumn(column);
  }, []);

  if (profilingStatusLoading) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 700,
          margin: "auto",
          mt: 16,
          border: "1px solid #dacdcd",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <img
            src={heartLoading}
            alt="loading"
            className="h-[100px] block m-auto animate-pulse"
          />
          <Typography variant="h5" component="h2" sx={{ mt: 2, mb: 1 }}>
            Profiling in Progress
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {completedFiles}/{totalFiles} Completed
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Please wait while we analyze your data assets. This might take a few
            moments.
          </Typography>
          <LinearProgress />
        </Box>
      </Paper>
    );
  }
  if (resultsLoading) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 700,
          margin: "auto",
          mt: 16,
          border: "1px solid #dacdcd",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <img
            src={heartLoading}
            alt="loading"
            className="h-[100px] block m-auto animate-pulse"
          />
          <Typography variant="h5" component="h2" sx={{ mt: 2, mb: 1 }}>
            Loading Results...
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Please wait while we load your profiling results.
          </Typography>
          <LinearProgress />
        </Box>
      </Paper>
    );
  }
  if (error) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 700,
          margin: "auto",
          mt: 16,
          border: "1px solid #dacdcd",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h5"
            color="error"
            sx={{ mt: 2, mb: 2, fontWeight: 600 }}
          >
            Profiling Failed
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={handleReRunProfiling}
            disabled={isReRunning}
            sx={{
              backgroundColor: "#bb1ccc",
              "&:hover": {
                backgroundColor: "#000080",
              },
              px: 4,
              py: 1.5,
              borderRadius: 2,
            }}
          >
            {isReRunning ? "Restarting..." : "Re-run Profiling"}
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Box
      sx={{ maxWidth: "90%", margin: "auto", mt: 4 }}
      className="  rounded pb-4 "
    >
      {finalResult && (
        <>
          <Box sx={{ mb: 4, p: 3,  display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: "#bb1ccc",
                fontWeight: 700,
                fontSize: "1.5rem",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: "4px",
                  height: "24px",
                  backgroundColor: "#bb1ccc",
                  borderRadius: "2px",
                }}
              />
              Profiling Results
            </Typography>
            
            <Button
              variant="contained"
              sx={{ backgroundColor: "#bb1ccc" }}
              onClick={handleReadyForDQ}
            >
              Ready to Run DQ Checks
            </Button>
          </Box>
          <Box sx={{ px: 4, pb: 4 }}>
            {profilingResults.map((result, index) => (
              <Box
                key={result.data_asset_name}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                  padding: 3,
                  marginBottom: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#2c3e50",
                        fontSize: "1.1rem",
                      }}
                    >
                      {result.data_asset_name}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#bb1ccc",
                      color: "white",
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "0.9rem",
                      "&:hover": {
                        backgroundColor: "#000080",
                      },
                    }}
                    onClick={() => {
                      setSelectedResult(result?.columns);
                      setModalOpen(true);
                      setPopupAssetName(result.data_asset_name);
                      setCurrentRowCount(result?.columns[0]?.count);
                    }}
                  >
                    View Result
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
          
        </>
      )}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <div className="flex justify-between items-center ">
          <DialogTitle sx={{ fontWeight: 700, fontSize: 20 }}>
            {popupAssetName}
          </DialogTitle>

          <IconButton
            sx={{ marginRight: "20px" }}
            onClick={() => setModalOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 4, mb: 2 }}>
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                padding: 3,
                width: "200px",
                height: "120px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                border: "1px solid #d3d3d3",
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: "0 12px 30px rgba(0, 0, 0, 0.2) !important",
                  transform: "translateY(-6px) !important",
                  border: "1px solid #bb1ccc !important",
                  backgroundColor: "#f8f9ff",
                },
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#bb1ccc", fontSize: "1.1rem" }}
                >
                  Columns Analyzed
                </Typography>
                <Box
                  sx={{
                    height: "3px",
                    width: "40px",
                    backgroundColor: "#bb1ccc",
                    borderRadius: "2px",
                    mt: 0.5,
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "#3C3C3C", fontSize: "2rem" }}
              >
                {selectedResult?.length || 0}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                padding: 3,
                width: "200px",
                height: "120px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                border: "1px solid #d3d3d3",
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: "0 12px 30px rgba(0, 0, 0, 0.2) !important",
                  transform: "translateY(-6px) !important",
                  border: "1px solid #bb1ccc !important",
                  backgroundColor: "#f8f9ff",
                },
              }}
            >
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: "#bb1ccc", fontSize: "1.1rem" }}
                >
                  Rows Profiled
                </Typography>
                <Box
                  sx={{
                    height: "3px",
                    width: "40px",
                    backgroundColor: "#bb1ccc",
                    borderRadius: "2px",
                    mt: 0.5,
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "#3C3C3C", fontSize: "2rem" }}
              >
                {currentRowCount || "N/A"}
              </Typography>
            </Box>
          </Box>
          <ProfilingResultsTable
            rows={selectedResult || []}
            onViewPlot={handleViewPlot}
          />
        </DialogContent>
      </Dialog>
      {/* Plotly Graph Popup */}
      <Dialog
        open={graphOpen}
        onClose={() => setGraphOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <div className="flex justify-end items-center p-4">
          <IconButton onClick={() => setGraphOpen(false)}>
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent sx={{ p: 0 }}>
          <Plot
            data={graphColumn?.chart?.data || []}
            layout={{
              ...(graphColumn?.chart?.layout || {}),
              margin: {
                t: 60,
                b: 80,
                ...(graphColumn?.chart?.layout?.margin || {}),
              },
              autosize: true,
              width: null,
              height: 500,
            }}
            style={{ width: "100%", height: "100%" }}
            useResizeHandler={true}
            config={{
              displayModeBar: false,   // <-- hides the mode bar
            }}
          />
        </DialogContent>
      </Dialog>
      <Toaster position="top-right" />
    </Box>
  );
};

export default DataProfiling;
